import type { Card } from "@amzn/ads-blink-client";
import { Heading, Text, Flex, TextButton } from "@amzn/storm-ui";
import { range } from "lodash";
import styled from "styled-components";
import { useStore } from "zustand";

import { StyleConstants } from "../../../../config/constants";
import { initialUnifiedFilterState } from "../../../../config/filters/unified";
import { INSIGHT_TEMPLATES } from "../../../../constants/filters";
import { useAiRecommendedInsightsLoader } from "../../../../hooks/aiRecommendedInsights/useAiRecommendedInsightsLoader";
import { useInsightTemplatesLoader } from "../../../../hooks/insightTemplates/useInsightTemplatesLoader";
import {
    FeatureFlags,
    FeatureFlagsStore,
} from "../../../../store/featureFlags";
import { InsightDeckTemplateTheme } from "../../../../types/providers";
import { isLoading, isNotExecutedOrLoading } from "../../../../utils/api.utils";
import SkeletonLoader from "../../../common/SkeletonLoader";
import { AiResultsViewer } from "../aiResults/AiResultsViewer";
import { UnifiedContentCard } from "../unifiedContent/UnifiedContentCard";
import {
    StyledBackground,
    StyledInsightContentGridRenderer,
} from "../withInsightsRenderer";

interface InsightTemplatesViewerProps {}

const StyledInsightTemplatesViewer = styled(StyledBackground)`
    padding-top: ${({ theme }) => theme.spacing.small.default};
    margin-bottom: ${({ theme }) => theme.spacing.small.default};
`;

const StyledTemplateSectionContainer = styled.div`
    margin-bottom: ${({ theme }) => theme.spacing.small.default};
`;

const SectionHeader = styled(Flex)`
    align-items: center;
    margin-bottom: ${({ theme }) => theme.spacing.xsmall.default};
`;

// Template type labels mapping
const templateTypeLabels: Record<InsightDeckTemplateTheme, string> = {
    [InsightDeckTemplateTheme.AD_PRODUCTS]: "Ad product templates",
    [InsightDeckTemplateTheme.ADVERTISER_GOALS]: "Advertiser goal templates",
    [InsightDeckTemplateTheme.DOWNSTREAM_IMPACT]: "Downstream impact templates",
    [InsightDeckTemplateTheme.INDUSTRY_INSIGHTS]: "Industry insights templates",
    [InsightDeckTemplateTheme.SEASONAL_EVENTS]: "Seasonal event templates",
};

export const InsightTemplatesViewer = (
    props: InsightTemplatesViewerProps,
): JSX.Element => {
    const [templateLoadingStates, templateGroups, templateCounts] =
        useInsightTemplatesLoader({});

    const isAiSearchEnabled = useStore(
        FeatureFlagsStore,
        (state) => state[FeatureFlags.AI_SEARCH],
    );

    const [aiLoadingState, aiCards, _aiRecommendations] =
        useAiRecommendedInsightsLoader();
    const showAiResults =
        isAiSearchEnabled &&
        (aiLoadingState !== "not-executed" || aiCards.length > 0);

    const getCardForContentV2 = (content: Card, index: number) => {
        const filterConfig =
            initialUnifiedFilterState.filters.UNIFIED_TYPE.find(
                (filter) => filter.id === content.header,
            );
        return (
            <UnifiedContentCard
                key={`${content.identifier}-${index}`}
                content={{
                    ...content,
                    header: INSIGHT_TEMPLATES,
                }}
                index={index}
            />
        );
    };

    const handleSeeAllClick = (templateType: InsightDeckTemplateTheme) => {
        // TODO: Implement navigation to filtered view
    };

    const renderTemplateSection = (templateType: InsightDeckTemplateTheme) => {
        const cards = templateGroups[templateType] || [];
        const count = templateCounts[templateType] || 0;
        const loadingState = templateLoadingStates[templateType];
        const isError = loadingState === "error";
        const hasContent = cards.length > 0;

        return (
            <StyledTemplateSectionContainer key={templateType}>
                <SectionHeader columnGap="xsmall">
                    <Heading renderAs="h4" size="small">
                        {templateTypeLabels[templateType]}
                    </Heading>
                    {hasContent && !isLoading(loadingState) && (
                        <>
                            <Text>({count} total)</Text>
                            <TextButton
                                onClick={() => handleSeeAllClick(templateType)}
                            >
                                See all
                            </TextButton>
                        </>
                    )}
                </SectionHeader>

                {isNotExecutedOrLoading(loadingState) ? (
                    <StyledInsightContentGridRenderer
                        maxResults={4}
                        noMarginTop
                        hidden={false}
                    >
                        {range(0, 4).map((row, index) => (
                            <SkeletonLoader
                                height={`${StyleConstants.InsightCardSize}px`}
                                width="100%"
                                key={`${row}_${index}`}
                            />
                        ))}
                    </StyledInsightContentGridRenderer>
                ) : isError ? (
                    <Text color="secondary">
                        Failed to load{" "}
                        {templateTypeLabels[templateType].toLowerCase()}
                    </Text>
                ) : hasContent ? (
                    <StyledInsightContentGridRenderer
                        maxResults={4}
                        noMarginTop
                        hidden={false}
                    >
                        {cards
                            .slice(0, 4)
                            .map((card, index) =>
                                getCardForContentV2(card, index),
                            )}
                    </StyledInsightContentGridRenderer>
                ) : (
                    <Text color="secondary">
                        No {templateTypeLabels[templateType].toLowerCase()}{" "}
                        available
                    </Text>
                )}
            </StyledTemplateSectionContainer>
        );
    };

    return (
        <StyledInsightTemplatesViewer data-testid="insights-renderer">
            {showAiResults && <AiResultsViewer />}
            {Object.values(InsightDeckTemplateTheme).map((templateType) =>
                renderTemplateSection(templateType),
            )}
        </StyledInsightTemplatesViewer>
    );
};
